import { useState, useEffect } from 'react';
import { examResultsAPI, tryoutAdminAPI, exportAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Search, Download, Printer, FileText, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA',
  'SMA Favorit'
];

export default function ExamResultsTab() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [tryouts, setTryouts] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    program: 'all',
    tryoutId: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const itemsPerPage = 10;

  useEffect(() => {
    loadTryouts();
  }, []);

  useEffect(() => {
    loadResults();
  }, [currentPage, filters]);

  const loadTryouts = async () => {
    try {
      const res = await tryoutAdminAPI.getAll();
      setTryouts(res.data);
    } catch (error) {
      console.error('Error loading tryouts:', error);
    }
  };

  const loadResults = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.program !== 'all') params.program = filters.program;
      if (filters.tryoutId !== 'all') params.tryout_id = filters.tryoutId;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      
      const res = await examResultsAPI.getAll(params);
      setResults(res.data.results || []);
      setTotalCount(res.data.total || 0);
    } catch (error) {
      toast.error('Gagal memuat data hasil ujian');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadResults();
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.program !== 'all') params.program = filters.program;
      if (filters.tryoutId !== 'all') params.tryout_id = filters.tryoutId;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      
      const res = await examResultsAPI.export(params);
      
      // Convert to CSV
      const data = res.data.data;
      if (data.length === 0) {
        toast.error('Tidak ada data untuk diexport');
        return;
      }
      
      const headers = Object.keys(data[0]).join(',');
      const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(','));
      const csv = [headers, ...rows].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `hasil_ujian_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Berhasil export ${data.length} data`);
    } catch (error) {
      toast.error('Gagal export data');
    }
  };

  const handleExportExcel = () => {
    // Build params for Excel export
    const params = {};
    if (filters.tryoutId !== 'all') params.tryout_id = filters.tryoutId;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sesi login tidak valid');
      return;
    }
    
    // Create URL with auth header via fetch then trigger download
    const url = exportAPI.resultsExcelUrl(params);
    
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(res => {
      if (!res.ok) throw new Error('Export gagal');
      return res.blob();
    })
    .then(blob => {
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `hasil_ujian_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success('Berhasil export ke Excel');
    })
    .catch(err => {
      console.error('Excel export error:', err);
      toast.error('Gagal export ke Excel');
    });
  };

  const handlePrint = () => {
    const printContent = document.getElementById('results-table');
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Hasil Nilai Ujian - MyTentor</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; color: #4472C4; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #4472C4; color: white; padding: 10px; text-align: left; }
            td { padding: 8px; border-bottom: 1px solid #ddd; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .print-date { text-align: right; color: #666; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>Hasil Nilai Ujian</h1>
          <p class="print-date">Tanggal Cetak: ${new Date().toLocaleDateString('id-ID')}</p>
          ${printContent.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Filter tryouts by program
  const filteredTryouts = filters.program !== 'all' 
    ? tryouts.filter(t => t.category === filters.program)
    : tryouts;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Hasil Nilai Ujian</h1>
        <div className="flex gap-2">
          <Button onClick={handleExportExcel} variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={handleExport} variant="outline" className="border-[#4472C4] text-[#4472C4]">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handlePrint} className="bg-[#4472C4] hover:bg-[#3461B3]">
            <Printer className="w-4 h-4 mr-2" />
            Cetak
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-sm text-slate-500">No Induk / Nama</Label>
              <Input
                placeholder="Cari..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            
            <div>
              <Label className="text-sm text-slate-500">Jenjang</Label>
              <Select 
                value={filters.program} 
                onValueChange={(v) => setFilters({ ...filters, program: v, tryoutId: 'all' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Semua Jenjang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenjang</SelectItem>
                  {PROGRAMS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-slate-500">Nama Ujian</Label>
              <Select 
                value={filters.tryoutId} 
                onValueChange={(v) => setFilters({ ...filters, tryoutId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Semua Ujian" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Ujian</SelectItem>
                  {filteredTryouts.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-slate-500">Tgl Ujian</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>

            <div className="flex items-end">
              <Button type="submit" className="w-full bg-[#4472C4] hover:bg-[#3461B3]">
                <Search className="w-4 h-4 mr-2" />
                Cari
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Hasil Nilai Ujian ({totalCount} data)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-slate-500">Memuat...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Belum ada data hasil ujian</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="results-table" className="w-full">
                <thead className="bg-[#4472C4] text-white">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm">No</th>
                    <th className="px-3 py-3 text-left text-sm">No Induk</th>
                    <th className="px-3 py-3 text-left text-sm">Nama</th>
                    <th className="px-3 py-3 text-left text-sm">Nama Ujian</th>
                    <th className="px-3 py-3 text-left text-sm">Jenjang</th>
                    <th className="px-3 py-3 text-left text-sm">Tgl Ujian</th>
                    <th className="px-3 py-3 text-center text-sm">Benar</th>
                    <th className="px-3 py-3 text-center text-sm">Salah</th>
                    <th className="px-3 py-3 text-center text-sm">Kosong</th>
                    <th className="px-3 py-3 text-center text-sm">Nilai</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {results.map((result, idx) => (
                    <tr key={result.id} className="hover:bg-slate-50">
                      <td className="px-3 py-3 text-sm">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className="px-3 py-3 text-sm font-medium">{result.user_code || '-'}</td>
                      <td className="px-3 py-3 text-sm">{result.user_name}</td>
                      <td className="px-3 py-3 text-sm">{result.tryout_title}</td>
                      <td className="px-3 py-3 text-sm">{result.program}</td>
                      <td className="px-3 py-3 text-sm">
                        {new Date(result.submitted_at).toLocaleDateString('id-ID')}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-green-600 font-medium">
                        {result.correct_count}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-red-600 font-medium">
                        {result.wrong_count || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-slate-500">
                        {result.blank_count || 0}
                      </td>
                      <td className="px-3 py-3 text-sm text-center font-bold text-[#4472C4]">
                        {Math.round(result.percentage || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              <span className="px-4 text-sm">
                {currentPage} / {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
